import { CircularProgress, Autocomplete, TextField, Chip, Tooltip, Checkbox } from "@mui/material";
import { useTranslations } from "@fenetech/translations";
import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from "react-hook-form";
import React from "react";
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

interface IProps {
    dataSource: any[] | null
    isAllSelected: boolean
    fieldName: string
    valueField: string
    displayField: string
    translateDisplayField: boolean
    label: string
    readOnly: boolean
    size?: "small" | "medium"
    validateValue?: ((value: any) => string | boolean) | undefined
    onChangeCallback?: ((value: any) => void) | undefined
    optionDisabled?: ((item: any) => boolean) | undefined
    optionTooltip?: ((item: any) => string) | undefined
    optionTooltipIsWarning?: ((item: any) => boolean) | undefined
    forceErrorMessage?: string
}

const AutocompleteMultiSelectForm: React.FC<IProps> = ({ dataSource, isAllSelected, fieldName, valueField, displayField, translateDisplayField, label,
    readOnly, size, validateValue, onChangeCallback, optionDisabled, optionTooltip, optionTooltipIsWarning, forceErrorMessage, ...rest }: IProps) => {

    const tm = useTranslations();
    const { control } = useFormContext();

    const { field, formState: { errors } } = useController({
        name: fieldName,
        control,
        rules: { validate: validateValue ?? undefined },
    });

    const allSelectedChip = useMemo(() => {
        let result = {};
        result[valueField] = -1;
        result[displayField] = `{${tm.Get("All")}}`;
        return result;
    }, [valueField, displayField, tm])

    const dataSourceInternal = useMemo(() => {
        if (isAllSelected) {
            return [allSelectedChip];
        }
        return dataSource;
    }, [isAllSelected, allSelectedChip, dataSource]);

    const selectedValues = useMemo(() => {
        if (isAllSelected) {
            return [allSelectedChip];
        }
        if (!field.value || !dataSource) {
            return [];
        }
        return dataSource.filter(x => field.value && field.value.includes(x[valueField]));
    }, [isAllSelected, allSelectedChip, dataSource, field.value, valueField]);

    const onChange = useCallback((_, data: any[]) => {
        const valueFieldArray: any[] = data.map((x => x[valueField]));
        field.onChange(valueFieldArray)
        if (onChangeCallback) {
            onChangeCallback(data);
        }
    }, [valueField, field, onChangeCallback]);

    const getOptionLabel = useCallback((option: any) => {
        if (!option) { return "" }

        if (translateDisplayField) {
            return tm.Get(option[displayField]);
        }
        return option[displayField];
    }, [tm, translateDisplayField, displayField]);

    const getTooltipAndIcon = useCallback((option: any): [JSX.Element | string, JSX.Element] => {
        const tooltipText = optionTooltip ? optionTooltip(option) : "";
        const tooltipIsWarning = optionTooltipIsWarning ? optionTooltipIsWarning(option) : false;

        // style helps format newlines in the tooltip correctly
        const tooltip = tooltipText ? <span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span> : "";
        const tooltipIcon = tooltip ? (tooltipIsWarning ? <WarningIcon sx={{ paddingLeft: 0.5 }} /> : <InfoIcon />) : <></>;

        return [tooltip, tooltipIcon];
    }, [optionTooltip, optionTooltipIsWarning]);

    return (
        <Autocomplete
            id={fieldName}
            autoComplete
            autoHighlight
            selectOnFocus
            disableClearable
            disabled={readOnly || isAllSelected}
            limitTags={15}
            blurOnSelect={false}
            handleHomeEndKeys
            options={dataSourceInternal ?? []}
            multiple
            fullWidth
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={getOptionLabel}
            getOptionDisabled={optionDisabled}
            renderInput={(params) => (
                <TextField
                    variant="outlined"
                    {...params}
                    label={label}
                    error={(forceErrorMessage || errors[fieldName]?.message) ? true : false}
                    helperText={forceErrorMessage ?? errors[fieldName]?.message}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {(dataSource === undefined || !dataSource) && <CircularProgress color="inherit" size={20} />}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
            renderOption={(props, option: any, { selected }) => (
                <li {...props}>
                    <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                    />
                    {option[displayField]}
                </li>
            )}
            renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    const disabled = optionDisabled ? optionDisabled(option) : false;
                    const [tooltip, icon] = getTooltipAndIcon(option);
                    return (
                        <Tooltip title={tooltip} key={key}>
                            <span>
                                <Chip
                                    icon={icon}
                                    label={getOptionLabel(option)}
                                    {...tagProps}
                                    disabled={disabled || (isAllSelected && !readOnly)}
                                    deleteIcon={(readOnly || disabled || isAllSelected) ? <></> : <CancelIcon />}
                                />
                            </span>
                        </Tooltip>
                    );
                })
            }
            onChange={onChange}
            value={selectedValues}
            ref={field.ref}
            size={size}
            {...rest}
        />
    );
}

export default AutocompleteMultiSelectForm;